// src/components/directory/DirectoryView.jsx
// ─────────────────────────────────────────────────────────────
// The main directory page. Composes:
//   - DirectoryHeader (nav + restart)
//   - SearchBar + FilterBar (DS-01..05)
//   - ResultsIntro (WZ-03 summary strip)
//   - ProgramGrid (PL-01, PL-05)
//   - ChatSidebar (PL-06)
//   - DirectoryFooter (SU-01)
// ─────────────────────────────────────────────────────────────

import React, { useState, useCallback } from 'react';
import { usePrograms } from '../../hooks/usePrograms';
import { useChat }     from '../../hooks/useChat';
import { track, EVENTS } from '../../lib/firebase';

import DirectoryHeader from './DirectoryHeader';
import SearchBar       from './SearchBar';
import FilterBar       from './FilterBar';
import ResultsIntro    from './ResultsIntro';
import ProgramGrid     from './ProgramGrid';
import ProgramModal    from './ProgramModal';
import ChatSidebar     from './ChatSidebar';
import DirectoryFooter from './DirectoryFooter';
import styles          from './DirectoryView.module.css';

export default function DirectoryView({ wizard }) {
  // ── Filter state ──────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [category,   setCategory]   = useState('all');
  const [province,   setProvince]   = useState(
    wizard.answers?.province || 'all'
  );
  const [freeOnly,   setFreeOnly]   = useState(
    wizard.answers?.refinement === 'free'
  );
  const [noReferral, setNoReferral] = useState(
    wizard.answers?.refinement === 'no-referral'
  );
  const [familyOK,   setFamilyOK]   = useState(
    wizard.answers?.refinement === 'family-access'
  );

  // ── Modal state ───────────────────────────────────────
  const [selectedProgram, setSelectedProgram] = useState(null);

  // ── Program data ──────────────────────────────────────
  const wizardFilters = wizard.toFilters();
  const { programs, loading, error } = usePrograms({
    searchTerm,
    category,
    province,
    freeOnly,
    noReferral,
    familyOK,
    personas:   wizardFilters.personas,
    challenges: wizardFilters.challenges,
  });

  // ── Chat ──────────────────────────────────────────────
  const chat = useChat({ wizardAnswers: wizard.answers });

  // ── Handlers ──────────────────────────────────────────
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleCategoryChange = useCallback((cat) => {
    setCategory(cat);
    track(EVENTS.FILTER_APPLIED, { filter: 'category', value: cat });
  }, []);

  const handleProvinceChange = useCallback((prov) => {
    setProvince(prov);
    track(EVENTS.FILTER_APPLIED, { filter: 'province', value: prov });
  }, []);

  const handleProgramClick = useCallback((program) => {
    setSelectedProgram(program);
    track(EVENTS.PROGRAM_VIEWED, { programId: program.id, title: program.title });
  }, []);

  return (
    <div className={styles.shell}>
      <DirectoryHeader onRestart={wizard.editAnswers} />

      <div className={styles.searchSection}>
        <ResultsIntro
          answers={wizard.answers}
          onEdit={wizard.editAnswers}
          visible={wizard.completed && (
            wizard.answers.who?.length > 0 ||
            wizard.answers.challenge?.length > 0 ||
            (wizard.answers.province && wizard.answers.province !== 'all')
          )}
        />
        <SearchBar
          value={searchTerm}
          onChange={handleSearch}
          onAiClick={() => track(EVENTS.AI_CHAT_OPENED)}
        />
        <FilterBar
          category={category}
          province={province}
          freeOnly={freeOnly}
          noReferral={noReferral}
          familyOK={familyOK}
          onCategoryChange={handleCategoryChange}
          onProvinceChange={handleProvinceChange}
          onFreeOnlyChange={setFreeOnly}
          onNoReferralChange={setNoReferral}
          onFamilyOKChange={setFamilyOK}
        />
      </div>

      <div className={styles.mainLayout}>
        <div className={styles.resultsCol}>
          <ProgramGrid
            programs={programs}
            loading={loading}
            error={error}
            hasWizardAnswers={
              wizard.answers.who?.length > 0 ||
              wizard.answers.challenge?.length > 0
            }
            onProgramClick={handleProgramClick}
          />
        </div>
        <aside className={styles.sidebarCol}>
          <ChatSidebar chat={chat} wizardAnswers={wizard.answers} />
        </aside>
      </div>

      <DirectoryFooter />

      {selectedProgram && (
        <ProgramModal
          program={selectedProgram}
          onClose={() => setSelectedProgram(null)}
        />
      )}
    </div>
  );
}
